import { useState, useEffect } from 'react'
import { useTranslation } from '../../core/hooks/useTranslation'
import Modal from './Modal'
import Input from './Input'
import { User } from '../../core/models/user'

interface DeleteAccountModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  user: User | null
  title: string
  description: string
  actionButtonText: string
  isLoading?: boolean
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  user,
  title,
  description,
  actionButtonText,
  isLoading = false
}: DeleteAccountModalProps) {
  const { t } = useTranslation()
  const [confirmationText, setConfirmationText] = useState('')

  // Reset confirmation text and submitting state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationText('')
      setIsSubmitting(false)
    }
  }, [isOpen])

  // Use last 5 digits of user phone number as confirmation keyword
  const phoneNumber = user?.phoneNumber || ''
  const confirmationKeyword = phoneNumber.slice(-5) // Last 5 digits
  const isConfirmed = confirmationText.trim() === confirmationKeyword

  // Get instruction text from translation and make digits bold
  const instructionBase = t('typeLast5Digits')
  const instructionParts = instructionBase.split('{digits}')

  // Get error text from translation
  const errorText = t('incorrectPhoneDigits')

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    if (isConfirmed && !isSubmitting) {
      setIsSubmitting(true)
      try {
        await onConfirm()
      } catch (error) {
        setIsSubmitting(false)
        throw error
      }
    }
  }

  const handleClose = () => {
    setConfirmationText('')
    onClose()
  }

  if (!user || !phoneNumber || phoneNumber.length < 5) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={description}
      actionButtonText={actionButtonText}
      onAction={handleConfirm}
      actionButtonLoading={isLoading || isSubmitting}
      actionButtonDisabled={!isConfirmed || isLoading || isSubmitting}
      actionButtonClassName="bg-red-600 text-white text-xs h-9 px-4 hover:bg-red-700 active:bg-red-800 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
      showCancelButton={true}
    >
      <div className="space-y-3 pt-2">
        <p className="text-sm text-gray-700 font-medium">
          {instructionParts[0]}
          <b>{confirmationKeyword}</b>
          {instructionParts[1]}
        </p>
        <Input
          type="tel"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder=""
          className="w-full"
          dir="ltr"
          maxLength={5}
        />
        {confirmationText && !isConfirmed && (
          <p className="text-xs text-red-600">
            {errorText}
          </p>
        )}
      </div>
    </Modal>
  )
}

